import React from "react";
import { cockpit } from "@/api";
import type { CustomerMailItem, CustomerMailView, MessageView } from "@/api";
import { pathOf } from "@/router";
import { useLiveInvalidation } from "@/live/live-provider";
import { Icon, cx, fmtStamp } from "@/lib";
import { StatusPill } from "@/ds";
import { useT } from "../../i18n";
import { reviewMessageEntries } from "./model";
import styles from "./email-review.module.css";

/** One row of the panel, whichever source it came from. */
interface MailRow {
  key: string;
  /** Belongs to the request on screen, so selecting it shows it in the message pane. */
  current: boolean;
  /** Where another request's row leads; null for the current request's rows. */
  route: string | null;
  requestNo: number | null;
  inbound: boolean;
  from: string | null;
  at: string | null;
  subject: string | null;
  preview: string;
}

function collapse(text: string | null | undefined): string {
  return (text ?? "").replace(/\s+/g, " ").trim();
}

/** The request's own messages, keyed exactly as the message pane keys them. */
function ownRows(messages: MessageView[]): MailRow[] {
  return reviewMessageEntries(messages).map(({ key, message }) => ({
    key,
    current: true,
    route: null,
    requestNo: null,
    inbound: message.direction === "inbound",
    from: message.fromAddr ?? null,
    at: message.receivedAt ?? null,
    subject: message.subject ?? null,
    preview: collapse(message.normalizedText),
  }));
}

function customerRows(items: CustomerMailItem[]): MailRow[] {
  return items.map((item) => ({
    key: item.id,
    current: item.current,
    route: item.current ? null : String(item.request_no ?? item.request_id),
    requestNo: item.request_no,
    inbound: item.direction === "inbound",
    from: item.from_addr,
    at: item.received_at,
    subject: item.subject,
    preview: item.preview,
  }));
}

function rowTime(row: MailRow): number | null {
  const time = row.at ? Date.parse(row.at) : Number.NaN;
  return Number.isNaN(time) ? null : time;
}

/**
 * The server's list plus any of the request's own messages it does not carry — cut off by its cap,
 * or arrived after it was read — so the request on screen never loses its own mail.
 */
function withOwnRows(server: MailRow[], own: MailRow[]): MailRow[] {
  const listed = new Set(server.map((row) => row.key));
  const missing = own.filter((row) => !listed.has(row.key));
  if (missing.length === 0) return server;
  return [...server, ...missing].sort((left, right) => {
    const leftTime = rowTime(left);
    const rightTime = rowTime(right);
    if (leftTime !== null && rightTime !== null) return rightTime - leftTime;
    if (leftTime !== null) return -1;
    if (rightTime !== null) return 1;
    return 0;
  });
}

function validView(view: CustomerMailView, requestId: string): CustomerMailView {
  if (
    !view
    || view.request_id !== requestId
    || !Array.isArray(view.messages)
    || view.messages.some((item) => typeof item?.id !== "string")
  ) {
    throw new TypeError("Customer mail response is not this request's list");
  }
  return view;
}

/** Current correspondence stays primary; other requests remain separate navigable history. */
export function CustomerMailRail({
  requestId,
  unconfirmedForm = false,
  messages,
  selectedMessageKey,
  unreadMessageKey,
  onSelectMessage,
  onOpenRequest,
  navigationDisabled,
}: {
  requestId: string;
  unconfirmedForm?: boolean;
  messages: MessageView[];
  selectedMessageKey: string | null;
  unreadMessageKey: string | null;
  onSelectMessage: (key: string) => void;
  onOpenRequest: (requestId: string) => void;
  /** Leaving the request would drop an open draft edit, so other requests stay shut meanwhile. */
  navigationDisabled: boolean;
}) {
  const t = useT();
  const currentHeadingId = React.useId();
  const historyHeadingId = React.useId();
  // Both are keyed by the request they answer, so switching requests never shows the last one's.
  const [loaded, setLoaded] = React.useState<{ requestId: string; view: CustomerMailView } | null>(null);
  const [failedFor, setFailedFor] = React.useState<string | null>(null);
  const sequence = React.useRef(0);
  const own = React.useMemo(() => ownRows(messages), [messages]);
  // A message arriving on this request, or ours being sent, changes what is newest.
  const ownSignature = own.map((row) => row.key).join(" ");

  const load = React.useCallback(async () => {
    const mine = ++sequence.current;
    try {
      const view = validView(await cockpit.listCustomerMail(requestId), requestId);
      if (mine !== sequence.current) return;
      setLoaded({ requestId, view });
      setFailedFor(null);
    } catch {
      if (mine === sequence.current) setFailedFor(requestId);
    }
  }, [requestId, unconfirmedForm]);

  React.useEffect(() => { void load(); }, [load, ownSignature]);
  const liveLoad = React.useRef(load);
  liveLoad.current = load;
  const invalidations = useLiveInvalidation("requests", "customers");
  React.useEffect(() => {
    if (invalidations > 0) void liveLoad.current();
  }, [invalidations]);

  const view = loaded?.requestId === requestId ? loaded.view : null;
  const failed = failedFor === requestId;
  const rows = view ? withOwnRows(customerRows(view.messages), own) : own;
  const currentRows = rows.filter((row) => row.current);
  const history = new Map<string, MailRow[]>();
  for (const row of rows) {
    if (row.current || row.route === null) continue;
    const group = history.get(row.route) ?? [];
    group.push(row);
    history.set(row.route, group);
  }
  // The message pane shows the newest message until one is picked; so does the selection here.
  const selectedKey = selectedMessageKey !== null && own.some((row) => row.key === selectedMessageKey)
    ? selectedMessageKey
    : own[0]?.key ?? null;

  const renderRow = (row: MailRow) => {
    const selected = row.current && row.key === selectedKey;
    const unread = row.current && row.key === unreadMessageKey;
    const className = cx(styles.railItem, selected && styles.railItemSelected, unread && styles.railItemUnread);
    const body = (
      <span className={styles.railItemBody}>
        <span className={styles.railItemTop}>
          <strong data-customer-mail-sender>{row.from || t("common.noInfo")}</strong>
          {row.at && <time>{fmtStamp(row.at)}</time>}
        </span>
        <span className={styles.railItemMeta}>
          <span className={styles.direction}>
            <Icon name={row.inbound ? "mailOpen" : "send"} size={13} />
            {t(row.inbound ? "ws.review.inbound" : "ws.review.outbound")}
          </span>
          {!row.current && row.requestNo !== null && (
            <span className={styles.railRequestBadge} data-customer-mail-request>
              {t("ws.customerMail.request", { n: row.requestNo })}
            </span>
          )}
          {/* #2181: the worklist's pill for a new reply. The selected reply is being
              acknowledged, so it only says so to assistive technology. */}
          {unread && (selected
            ? <span className="sr-only">{t("ws.review.unreadMessageState")}</span>
            : <StatusPill tone="info" variant="soft" size="sm" label={t("inbox.attention.new_reply")} data-customer-mail-new />)}
        </span>
        <span className={styles.railSubject}>{row.subject || t("common.noSubject")}</span>
        {row.preview && <span className={styles.preview}>{row.preview}</span>}
      </span>
    );
    const route = row.route;
    if (route === null) {
      return (
        <button
          key={row.key}
          type="button"
          className={className}
          aria-current={selected ? "true" : undefined}
          onClick={() => onSelectMessage(row.key)}
          data-customer-mail-key={row.key}
        >
          {body}
        </button>
      );
    }
    return (
      <a
        key={row.key}
        className={className}
        href={navigationDisabled ? undefined : pathOf({ name: "mail", id: route })}
        aria-disabled={navigationDisabled || undefined}
        tabIndex={navigationDisabled ? -1 : undefined}
        onClick={(event) => {
          if (navigationDisabled) {
            event.preventDefault();
            return;
          }
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          onOpenRequest(route);
        }}
        data-customer-mail-key={row.key}
      >
        {body}
      </a>
    );
  };

  return (
    <aside
      className={cx(styles.rail, styles.customerMailRail)}
      aria-label={t("ws.customerMail.title")}
    >
      <div className={styles.railList} data-testid="customer-mail-list">
        <section aria-labelledby={currentHeadingId}>
          <div className={styles.railHeader} data-testid="customer-mail-header">
            <h3 id={currentHeadingId}>{t("ws.customerMail.current")}</h3>
            <span className={styles.railCount}>{t("ws.review.messageCount", { n: currentRows.length })}</span>
          </div>
          {currentRows.length === 0 && <p className={styles.empty}>{t("ws.noMessages")}</p>}
          {currentRows.map(renderRow)}
        </section>
        {!unconfirmedForm && (
          <section className={styles.railHistory} aria-labelledby={historyHeadingId}>
            <div className={styles.railHeader}>
              <h3 id={historyHeadingId}>{t("ws.customerMail.history")}</h3>
            </div>
            <p className={styles.railNotice}>{t("ws.customerMail.historyHint")}</p>
            {!view && !failed && <p className={styles.railNotice}>{t("ws.customerMail.loading")}</p>}
            {failed && (
              <div className={cx(styles.railNotice, styles.railNoticeError)} role="status">
                <span>{t("ws.customerMail.failed")}</span>
                <button
                  type="button"
                  className={cx(styles.railRetry, "neo-btn neo-btn--secondary")}
                  onClick={() => { void load(); }}
                >
                  {t("ws.customerMail.retry")}
                </button>
              </div>
            )}
            {view && !failed && history.size === 0 && (
              <p className={styles.railNotice}>{t("ws.customerMail.noHistory")}</p>
            )}
            {[...history].map(([route, group]) => {
              const subject = group.find((row) => row.inbound && row.subject)?.subject
                ?? group.find((row) => row.subject)?.subject;
              return (
                <details key={`${requestId}:${route}`} className={styles.railHistoryRequest}>
                  <summary className={styles.railHistorySummary}>
                    <span className={styles.railHistoryHeading}>
                      <strong>{t("ws.customerMail.request", { n: group[0].requestNo ?? route })}</strong>
                      <span>{subject || t("common.noSubject")}</span>
                      <span className={styles.railCount}>{t("ws.review.messageCount", { n: group.length })}</span>
                    </span>
                  </summary>
                  {group.map(renderRow)}
                </details>
              );
            })}
            {view?.truncated && (
              <p className={styles.railNotice}>{t("ws.customerMail.truncated", { n: view.messages.length })}</p>
            )}
          </section>
        )}
      </div>
    </aside>
  );
}
